import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import React, { useEffect } from 'react'

type Props = {
    children: React.ReactNode
    title?: string
    onClose: () => void
    leftFooter?: React.ReactNode
    rightFooter?: React.ReactNode
    size?: 'small' | 'medium' | 'large' | 'xl' | 'custom' // Add predefined size options
    customWidth?: string // Optional custom width if 'custom' size is selected
    className?: string
    allowUserToClose?: boolean
}

export default function Modal({
    title,
    children,
    onClose,
    leftFooter,
    rightFooter,
    size = 'medium',
    customWidth,
    className,
    allowUserToClose = true,
}: Props): React.JSX.Element {
    // Prevent background scroll when modal is open
    useEffect(() => {
        const prevOverflow = document.body.style.overflow
        const prevPaddingRight = document.body.style.paddingRight
        // Compensate for scrollbar to avoid layout shift
        const scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth
        document.body.style.overflow = 'hidden'
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`
        }
        return () => {
            document.body.style.overflow = prevOverflow
            document.body.style.paddingRight = prevPaddingRight
        }
    }, [])

    const modalClasses = clsx(
        className,
        'bg-white rounded-2xl shadow-lg w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden',
        {
            'max-w-sm': size === 'small',
            'max-w-5xl': size === 'large',
            'max-w-7xl': size === 'xl',
            [customWidth || '']: size === 'custom', // Custom width fallback
        },
    )

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overscroll-none"
            onClick={allowUserToClose ? onClose : undefined}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={modalClasses}
                onClick={(e) => e.stopPropagation()} // Prevent closing modal when clicking inside it
            >
                {/* Header - only show if title is provided */}
                {title && (
                    <div className="flex justify-between items-center p-4 border-b">
                        <h2 className="text-lg font-semibold">{title}</h2>
                        {allowUserToClose && (
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:bg-gray-100 p-2 rounded-full w-8 h-8 flex items-center justify-center"
                            >
                                <FontAwesomeIcon
                                    icon={faXmark}
                                    className="text-xl"
                                />
                            </button>
                        )}
                    </div>
                )}

                {/* Close button for modals without title */}
                {!title && allowUserToClose && (
                    <div className="absolute top-4 right-4 z-10">
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:bg-gray-100 p-2 rounded-full w-8 h-8 flex items-center justify-center"
                        >
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="text-xl"
                            />
                        </button>
                    </div>
                )}

                {/* Main Content */}
                <div className="p-4 overflow-y-auto overscroll-contain flex-1">
                    {children}
                </div>

                {/* Footer */}
                {(leftFooter || rightFooter) && (
                    <div className="p-4 border-t flex justify-between">
                        <div>{leftFooter}</div>
                        <div className="flex space-x-2">{rightFooter}</div>
                    </div>
                )}
            </div>
        </div>
    )
}
